import type { Bracket, BracketMatch, BracketTeam, RGNEvent } from '@/lib/api/schemas'
import { MOCK_TOURNAMENT_TEAMS } from '@/features/marketing/mockData/tournament'

/**
 * Demo fixtures for the gated Tournament Stadium walkthrough.
 *
 * Conforms to the real RGNEvent + Bracket schemas so the live
 * Stadium phase components can render this directly — the demo IS
 * the real Stadium, just driven from a controlled fixture instead
 * of the polling hook.
 *
 * The fixture is "maximally populated": every field a downstream
 * phase might read is set, even if it logically contradicts (e.g.
 * `startTime` in the future AND `lifecycle: 'RUNNING'` AND
 * `finalStandings` populated). Each phase component only reads what
 * it needs, so contradictions don't cause errors — they let one
 * fixture serve every phase. The StadiumDemoPlayer just changes the
 * `phase` value passed to <PhaseBody>; nothing else needs to vary.
 *
 * Why not split into 5 phase-specific fixtures?
 *   Each phase reading from the SAME data keeps team identities and
 *   visuals consistent across the walkthrough — visitors see the
 *   same Orion in CHECK_IN that they see in the GRAND FINAL. A
 *   per-phase fixture would risk the Orion avatar drifting between
 *   screens, which is exactly the "feels fake" smell we're trying
 *   to avoid.
 */

const { ORION, STORMBREAKER, PALADIN, SABER, TEMPEST, CYPHER, JUNO, ROOK } =
  MOCK_TOURNAMENT_TEAMS

// Anchor times. We pin to a stable demo "now" so the rendered
// countdown / "X minutes ago" copy doesn't drift between page loads
// in a way that breaks the choreography. The demo's "now" sits in
// CHECK_IN territory so the first phase the visitor sees has live-
// feeling timestamps.
const DEMO_NOW_MS = Date.parse('2026-06-16T19:30:00Z')
const START_MS = DEMO_NOW_MS + 15 * 60 * 1000          // 15 min from now
const CHECK_IN_OPENS_MS = DEMO_NOW_MS - 5 * 60 * 1000  // opened 5 min ago
const CHECK_IN_CLOSES_MS = DEMO_NOW_MS + 10 * 60 * 1000 // closes in 10 min

/**
 * Demo-time `now()` — used by the StadiumDemoPlayer to give phase
 * components a stable timestamp. Stadium's TEASER countdown wants
 * to be relative to "now"; pinning it lets the demo loop look the
 * same on every cycle.
 */
export const DEMO_NOW = DEMO_NOW_MS

// Build a BracketTeam from a MockTeam. The Stadium components read
// `members[0].avatarUrl` as the team mark when no team-level avatar
// is set, so we slot the SVG logo path into the captain's member
// record.
function toTeam(mock: typeof ORION, captainName: string): BracketTeam {
  return {
    teamId: mock.teamId,
    teamLabel: mock.teamLabel,
    captainId: `${mock.teamId}-captain`,
    members: [
      {
        userId: `${mock.teamId}-captain`,
        displayName: captainName,
        avatarUrl: mock.avatarUrl, // SVG path from /public/team-logos/
        callsign: mock.memberCallsign,
      },
    ],
  }
}

const T_ORION = toTeam(ORION, 'SGT Orion')
const T_STORMBREAKER = toTeam(STORMBREAKER, 'Stormbreaker')
const T_PALADIN = toTeam(PALADIN, 'Paladin')
const T_SABER = toTeam(SABER, 'Saber')
const T_TEMPEST = toTeam(TEMPEST, 'Tempest')
const T_CYPHER = toTeam(CYPHER, 'Cypher')
const T_JUNO = toTeam(JUNO, 'Juno')
const T_ROOK = toTeam(ROOK, 'Rook')

const ALL_TEAMS = [
  T_ORION, T_STORMBREAKER, T_PALADIN, T_SABER, T_TEMPEST, T_CYPHER, T_JUNO, T_ROOK,
]

/**
 * 8-team single-elim bracket in a snapshot state where:
 *   - All 4 quarterfinals complete
 *   - Both semifinals complete (Orion + Paladin advance)
 *   - Grand final IN_PROGRESS (Orion vs Paladin)
 *
 * This lets every phase render meaningfully:
 *   - CHECK_IN reads the entrants from event.attendees
 *   - LIVE reads the bracket and shows the semifinal column lit up
 *   - FINAL_MATCH zooms into the in-progress GF
 *   - COMPLETE reads finalStandings to render the podium
 */
const DEMO_MATCHES: BracketMatch[] = [
  // QFs
  m('qf1', 0, 0, T_ORION, T_SABER, T_ORION.teamId, 'COMPLETE'),
  m('qf2', 0, 1, T_STORMBREAKER, T_CYPHER, T_STORMBREAKER.teamId, 'COMPLETE'),
  m('qf3', 0, 2, T_PALADIN, T_JUNO, T_PALADIN.teamId, 'COMPLETE'),
  m('qf4', 0, 3, T_TEMPEST, T_ROOK, T_TEMPEST.teamId, 'COMPLETE'),
  // SFs
  m('sf1', 1, 0, T_ORION, T_STORMBREAKER, T_ORION.teamId, 'COMPLETE'),
  m('sf2', 1, 1, T_PALADIN, T_TEMPEST, T_PALADIN.teamId, 'COMPLETE'),
  // GF
  {
    matchId: 'gf',
    kind: 'W',
    round: 2,
    matchIndex: 0,
    bestOf: 3,
    participantA: T_ORION,
    participantB: T_PALADIN,
    // SCHEDULED is the schema enum for "match is on but not yet
    // reported as COMPLETE" — the Stadium treats anything that's
    // !COMPLETE as in-progress for the LIVE/FINAL_MATCH branching.
    status: 'SCHEDULED',
    winnerTeamId: null,
    nextMatchId: null,
    nextMatchSlot: null,
    isBye: false,
  },
]

function m(
  matchId: string,
  round: number,
  matchIndex: number,
  a: BracketTeam,
  b: BracketTeam,
  winnerTeamId: string,
  status: BracketMatch['status'],
): BracketMatch {
  return {
    matchId,
    kind: 'W',
    round,
    matchIndex,
    bestOf: 1,
    participantA: a,
    participantB: b,
    status,
    winnerTeamId,
    nextMatchId: null,
    nextMatchSlot: null,
    isBye: false,
  }
}

export const DEMO_BRACKET: Bracket = {
  bracketStatus: 'LOCKED_IN',
  bestOf: 1,
  lockedInAt: new Date(DEMO_NOW_MS - 10 * 60 * 1000).toISOString(),
  matches: DEMO_MATCHES,
  championTeamId: T_ORION.teamId,
  stage: {
    stageId: 'demo-stage',
    stageType: 'SINGLE_ELIM',
    status: 'RUNNING',
    config: { bestOf: 1, rounds: 3 },
  },
}

export const DEMO_EVENT: RGNEvent = {
  id: 'demo-event',
  title: 'Friday Bracket Night · CS2',
  description: 'Weekly tournament — single elim, £5 entry, £80 pot.',
  type: 'TOURNAMENT',
  status: 'LIVE',
  startTime: new Date(START_MS).toISOString(),
  endTime: new Date(START_MS + 3 * 60 * 60 * 1000).toISOString(),
  checkInOpensAt: new Date(CHECK_IN_OPENS_MS).toISOString(),
  checkInClosesAt: new Date(CHECK_IN_CLOSES_MS).toISOString(),
  gameId: 'cs2',
  gameName: 'Counter-Strike 2',
  gameShortCode: 'CS2',
  attendeeCount: 8,
  maxAttendees: 8,
  attendees: ALL_TEAMS.map((t, i) => ({
    userId: t.members![0]!.userId,
    callsign: t.members![0]!.callsign ?? null,
    displayName: t.members![0]!.displayName ?? null,
    avatarUrl: t.members![0]!.avatarUrl ?? null,
    isVerified: i < 5, // mix of verified + standard so the CHECK_IN row chrome lights up
    joinedAt: new Date(CHECK_IN_OPENS_MS + i * 30_000).toISOString(),
  })),
  attendeeIds: ALL_TEAMS.map((t) => t.members![0]!.userId),
  entryFeePence: 500,
  prizePoolPence: 8000,
  prizeStructure: {
    positions: 3,
    splits: [0.5, 0.3, 0.2],
  },
  tournamentFormat: 'SINGLE_ELIMINATION',
  bracketSize: 8,
  bracketStatus: 'LOCKED_IN',
  bracketLockedInAt: new Date(DEMO_NOW_MS - 10 * 60 * 1000).toISOString(),
  bestOf: 1,
  lifecycle: 'RUNNING',
  venue: 'The Foxhole · Bristol',
  squadronId: 'demo-squadron',
  participatingSquadronTags: ['BLK'],
  finalStandings: [
    {
      rank: 1,
      placementLabel: '1st',
      teamId: T_ORION.teamId,
      teamLabel: T_ORION.teamLabel,
      members: T_ORION.members!.map((m) => ({
        userId: m.userId,
        displayName: m.displayName ?? null,
        avatarUrl: m.avatarUrl ?? null,
      })),
    },
    {
      rank: 2,
      placementLabel: '2nd',
      teamId: T_PALADIN.teamId,
      teamLabel: T_PALADIN.teamLabel,
      members: T_PALADIN.members!.map((m) => ({
        userId: m.userId,
        displayName: m.displayName ?? null,
        avatarUrl: m.avatarUrl ?? null,
      })),
    },
    {
      rank: 3,
      placementLabel: '3rd',
      teamId: T_STORMBREAKER.teamId,
      teamLabel: T_STORMBREAKER.teamLabel,
      members: T_STORMBREAKER.members!.map((m) => ({
        userId: m.userId,
        displayName: m.displayName ?? null,
        avatarUrl: m.avatarUrl ?? null,
      })),
    },
  ],
  finalStandingsAt: new Date(DEMO_NOW_MS + 90 * 60 * 1000).toISOString(),
}
